import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useState } from 'react';

export default function Home() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d4j4a1f1vras6h6ft62g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for subscribing! We\'ll keep you updated.');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Unable to subscribe. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: 'ri-time-line',
      title: 'Rapid Access to Treatment',
      description: 'Same-week scheduling with precision coordination. Our streamlined protocols ensure you begin therapy within 48-72 hours of physician referral and authorization.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Comprehensive Authorization Management',
      description: 'Our dedicated benefits investigation team handles all insurance verification, prior authorizations, and appeals—eliminating administrative burden for patients and providers.'
    },
    {
      icon: 'ri-hospital-line',
      title: 'Hospital-Grade Standards',
      description: 'Advanced monitoring protocols and pharmaceutical-grade safety measures in a private, comfortable outpatient environment designed for optimal patient outcomes.'
    },
    {
      icon: 'ri-stethoscope-line',
      title: 'Physician-Directed Protocols',
      description: 'Every infusion follows strict medical protocols with continuous clinical monitoring, real-time physician oversight, and immediate intervention capability.'
    }
  ];

  const services = [
    {
      name: 'IVIG Therapy',
      icon: 'ri-shield-cross-line',
      description: 'Immunoglobulin replacement therapy for primary immunodeficiencies, CIDP, myasthenia gravis, and complex autoimmune conditions'
    },
    {
      name: 'Iron Replacement',
      icon: 'ri-heart-pulse-line',
      description: 'Advanced iron formulations including Injectafer, Venofer, and Feraheme for severe anemia and iron deficiency disorders'
    },
    {
      name: 'Biologic Infusions',
      icon: 'ri-medicine-bottle-line',
      description: 'Monoclonal antibody therapies including Remicade biosimilars, Entyvio, Stelara, Skyrizi for inflammatory and autoimmune diseases'
    },
    {
      name: 'Specialty Protocols',
      icon: 'ri-flask-line',
      description: 'Physician-ordered specialty infusions with customized protocols for complex medical conditions requiring expert administration'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        <section 
          className="relative bg-cover bg-center min-h-[650px] flex items-center"
          style={{
            backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.88), rgba(248, 249, 250, 0.92)), url('https://public.readdy.ai/ai/img_res/197cab2a887d7c2fcf215ce16ff7ef7f.jpg')`
          }}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 w-full">
            <div className="max-w-3xl">
              <h1 className="text-5xl lg:text-6xl font-bold text-[#0f1e32] mb-6 leading-tight">
                Specialty Infusions Delivered With Skill, Comfort, and Care
              </h1>
              <p className="text-xl text-gray-700 mb-10 leading-relaxed max-w-2xl">
                Expert IV therapy for IVIG, iron, and biologic treatments in a calm, private setting with quick scheduling and attentive clinical oversight.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/physicians">
                  <Button variant="primary" size="lg" className="!px-8 !py-4">
                    <i className="ri-file-text-line mr-2"></i>
                    Physician Referral Portal
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="secondary" size="lg" className="!px-8 !py-4">
                    <i className="ri-calendar-line mr-2"></i>
                    Schedule Appointment
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#0f1e32] mb-5">Why Patients and Physicians Choose Zeus</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Precision-led infusion care with hospital-grade standards in a private, comfortable outpatient environment.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl p-7 hover:shadow-xl transition-all border border-gray-100">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#0f1e32] to-[#1e3a5f] rounded-lg flex items-center justify-center mb-5 shadow-md">
                    <i className={`${feature.icon} text-3xl text-[#d4af37]`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#0f1e32] mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-[#f8f9fa] to-[#ffffff]">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#0f1e32] mb-5">Comprehensive Specialty Infusion Services</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Physician-directed infusion therapy for complex medical conditions with continuous monitoring and advanced clinical protocols.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, index) => (
                <div key={index} className="bg-white rounded-xl p-7 shadow-md hover:shadow-xl transition-all border border-gray-100">
                  <div className="w-14 h-14 bg-[#d4af37]/10 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${service.icon} text-3xl text-[#0f1e32]`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#0f1e32] mb-3">{service.name}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">{service.description}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-14">
              <Link to="/services">
                <Button variant="primary" size="lg" className="!px-9 !py-3">
                  Explore All Services
                  <i className="ri-arrow-right-line ml-2"></i>
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20medical%20infusion%20monitoring%20equipment%20and%20IV%20therapy%20setup%20in%20serene%20clinical%20environment%2C%20sophisticated%20pharmaceutical%20preparation%20area%20with%20modern%20technology%20and%20soft%20lighting%2C%20pristine%20medical%20workspace%20with%20precision%20instruments%20and%20calming%20atmosphere%2C%20elegant%20clinical%20setting%20with%20natural%20light%20and%20professional%20equipment%2C%20advanced%20infusion%20therapy%20technology%20in%20peaceful%20healthcare%20facility%2C%20modern%20medical%20treatment%20room%20photography&width=700&height=550&seq=clinical-calm-002&orientation=landscape"
                  alt="Advanced clinical protocols at Zeus Infusion"
                  className="rounded-2xl shadow-xl w-full h-[550px] object-cover object-top"
                />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-[#0f1e32] mb-7">Precision-Driven Infusion Protocols</h2>
                <p className="text-lg text-gray-600 mb-7 leading-relaxed">
                  Zeus Specialty Infusion Clinic operates with hospital-grade standards in a private outpatient environment. Our critical-care trained clinical staff follows meticulous protocols to ensure optimal safety and therapeutic outcomes.
                </p>
                <div className="space-y-5">
                  <div className="flex items-start space-x-4">
                    <div className="w-11 h-11 bg-gradient-to-br from-[#d4af37] to-[#c19b2a] rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-[#0f1e32] font-bold text-lg">1</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0f1e32] mb-1 text-base">Physician Coordination</h4>
                      <p className="text-gray-600 leading-relaxed text-sm">Seamless communication with referring specialists. Orders and laboratory results transmitted via secure channels.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-11 h-11 bg-gradient-to-br from-[#d4af37] to-[#c19b2a] rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-[#0f1e32] font-bold text-lg">2</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0f1e32] mb-1 text-base">Benefits Investigation & Authorization</h4>
                      <p className="text-gray-600 leading-relaxed text-sm">Comprehensive insurance verification and prior authorization management handled entirely by our specialized team.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-11 h-11 bg-gradient-to-br from-[#d4af37] to-[#c19b2a] rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-[#0f1e32] font-bold text-lg">3</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0f1e32] mb-1 text-base">Rapid Scheduling</h4>
                      <p className="text-gray-600 leading-relaxed text-sm">Same-week availability with direct patient contact. Treatment initiated within 48-72 hours of authorization approval.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-11 h-11 bg-gradient-to-br from-[#d4af37] to-[#c19b2a] rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-[#0f1e32] font-bold text-lg">4</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0f1e32] mb-1 text-base">Advanced Clinical Monitoring</h4>
                      <p className="text-gray-600 leading-relaxed text-sm">Continuous vital sign monitoring with immediate physician access. Private infusion suites designed for comfort and safety.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-[#0f1e32] to-[#1e3a5f] text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
              <div>
                <div className="text-5xl font-bold text-[#d4af37] mb-3">48-72h</div>
                <p className="text-xl font-light">Treatment Initiation</p>
                <p className="text-gray-300 mt-2 text-sm">From referral to first infusion</p>
              </div>
              <div>
                <div className="text-5xl font-bold text-[#d4af37] mb-3">100%</div>
                <p className="text-xl font-light">Authorization Management</p>
                <p className="text-gray-300 mt-2 text-sm">Complete insurance coordination</p>
              </div>
              <div>
                <div className="text-5xl font-bold text-[#d4af37] mb-3">Expert</div>
                <p className="text-xl font-light">Clinical Oversight</p>
                <p className="text-gray-300 mt-2 text-sm">Critical-care trained specialists</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
            <div className="mb-8">
              <i className="ri-mail-line text-5xl text-[#d4af37] mb-4"></i>
              <h2 className="text-3xl font-bold text-[#0f1e32] mb-4">Stay Informed</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Subscribe to receive updates on our services, clinical insights, and important information for patients and healthcare providers.
              </p>
            </div>
            
            <form 
              id="newsletter-form"
              data-readdy-form
              onSubmit={handleNewsletterSubmit}
              className="max-w-md mx-auto"
            >
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  className="flex-1 px-5 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] focus:border-transparent text-base"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-3 bg-[#0f1e32] text-white rounded-lg font-semibold hover:bg-[#1e3a5f] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </button>
              </div>
              {submitMessage && (
                <p className={`mt-4 text-sm ${submitMessage.includes('Thank you') ? 'text-green-600' : 'text-red-600'}`}>
                  {submitMessage}
                </p>
              )}
            </form>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-[#f8f9fa] to-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-4xl font-bold text-[#0f1e32] mb-6">Experience Advanced Infusion Care</h2>
            <p className="text-lg text-gray-600 mb-10 leading-relaxed">
              Whether you're a physician seeking a trusted specialty infusion partner or a patient requiring expert infusion therapy, Zeus Specialty Infusion Clinic delivers uncompromising clinical excellence in Los Angeles.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/physicians">
                <Button variant="primary" size="lg" className="!px-9 !py-3">
                  <i className="ri-hospital-line mr-2"></i>
                  Physician Resources
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="secondary" size="lg" className="!px-9 !py-3">
                  <i className="ri-phone-line mr-2"></i>
                  Contact Our Team
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
